import React, { useState, useEffect } from 'react';

// Base URL of the backend (HVAC-Backend project). Empty in development —
// CRA's "proxy" setting forwards /api requests to http://localhost:5000.
// In production builds, set REACT_APP_API_URL to the deployed backend URL.
const API_URL = process.env.REACT_APP_API_URL || '';

export default function Contact() {
  useEffect(() => {
    document.title = 'Contact Us | Seas Heating and AC';
  }, []);
  const [formState, setFormState] = useState({
    name: '',
    title: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSending(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormState({ name: '', title: '', description: '' });
      } else {
        setError('Something went wrong. Please try calling (702) 344-9797 instead.');
      }
    } catch {
      setError('Something went wrong. Please try calling (702) 344-9797 instead.');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <>
      {/* Page Header */}
      <section className="bg-seas-blue text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-white/80 text-sm font-semibold uppercase tracking-widest mb-2">Get in Touch</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight">Contact Us</h1>
          <p className="text-gray-300 max-w-2xl">
            Get in touch for a free quote, emergency service, or any HVAC questions. We're here to help.
          </p>
        </div>
      </section>

      {/* Intro + image section */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-seas-blue/80 text-sm font-semibold uppercase tracking-widest mb-2">Reach Out</p>
              <h2 className="text-2xl font-bold text-seas-gray mb-4 tracking-tight">We're Here When You Need Us</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Whether you need a scheduled tune-up, a free estimate for a new system, or emergency repair, 
                Seas Heating and AC is just a call or message away. We serve the greater Las Vegas area and 
                respond quickly to every inquiry.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Prefer to reach out online? Fill out the form and we'll get back to you as soon as possible. 
                For urgent issues, calling is always the fastest option.
              </p>
            </div>
            <img src="/images/contactus-image.png" alt="Contact Seas Heating and AC - Las Vegas HVAC" className="min-h-[280px] w-full object-cover rounded-xl" />
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 lg:py-24 bg-gray-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-xl font-bold text-seas-gray mb-6">Get in Touch</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-seas-gray mb-2">Phone</h3>
                  <a
                    href="tel:+17023449797"
                    className="text-seas-blue hover:text-seas-gold text-lg font-medium transition-colors"
                  >
                    (702) 344-9797
                  </a>
                  <p className="text-gray-500 text-sm mt-1">Call anytime for emergencies</p>
                </div>
                <div>
                  <h3 className="font-semibold text-seas-gray mb-2">Email</h3>
                  <a
                    href="mailto:info@seasheatingandac.com"
                    className="text-seas-blue hover:text-seas-gold font-medium transition-colors"
                  >
                    info@seasheatingandac.com
                  </a>
                  <p className="text-gray-500 text-sm mt-1">We typically respond within 24 hours</p>
                </div>
                <div>
                  <h3 className="font-semibold text-seas-gray mb-2">Service Area</h3>
                  <p className="text-gray-600">Las Vegas, Henderson, North Las Vegas, Summerlin & surrounding areas</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200">
                <h2 className="text-xl font-bold text-seas-gray mb-6">Send Us a Message</h2>

                {submitted && (
                  <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg">
                    Thank you! Your message has been sent. We'll get back to you soon.
                  </div>
                )}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg">{error}</div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formState.name}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-seas-gold focus:border-seas-gold"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      required
                      value={formState.title}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-seas-gold focus:border-seas-gold"
                      placeholder="What is this about?"
                    />
                  </div>
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows={5}
                      value={formState.description}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-seas-gold focus:border-seas-gold"
                      placeholder="Tell us about your HVAC needs..."
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full sm:w-auto bg-seas-gold text-white font-semibold px-8 py-3 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
